import CustomBreadcrumbs from "../../components/Tools/Breadcumbs";
import Spinner from "../../components/Tools/spinner";
import SubHeader from "../../components/Subheader/Subheader";
import React, {useState, useEffect, useRef} from 'react'
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { getConsolidateBeneficiaryPPAList, getExportConsolidateBeneficiaryPPAList, getBeneficiaryPPATrail, get_invoice_details_by_invoice_id } from '../../helper/Api';
import { useSelector } from 'react-redux';
import { DEFAULT_EXPENSE_DATE_BASIS } from '../../RTK/Slices/AppConfigSlice';
import { toast } from 'react-toastify';
import { AiOutlineVerticalAlignBottom } from 'react-icons/ai';
import { Tooltip, Button, IconButton } from '@mui/material';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import moment from "moment";
import MainFilter from "../../components/Tools/MainFilter";
import DashboardFilter from "../Dashboard/components/DashboardFilter";
import { AddCommasToAmount, downloadAuthBlobResponse, getBlobErrorMessage } from '../../helper/Utils';
import { openPpaDocumentOrToast } from '../../helper/secureDocuments';
import PpaSchemeInfoCell from '../../components/Ppa/PpaSchemeInfoCell';
import {
    PpaSchemeInfoExpandProvider,
    PpaSchemeInfoColumnHeader,
} from '../../components/Ppa/PpaSchemeInfoExpandContext';
import PpaDeductionDetailsCell from '../../components/Ppa/PpaDeductionDetailsCell';
import DepartmentIdentityCell from '../../components/Tools/DepartmentIdentityCell';
import InvoiceOverlay from '../../components/Disbursment/Components/InvoiceOverlay';
import PaymentTrailModal from '../../components/Reconciliation/PaymentTrailModal';

const EXCLUDED_INVOICE_ID_FIELDS = new Set([
    'id',
    'ppa_id',
    'ppa_invoice_batch_id',
    'beneficiary_reimbursement_id',
    'salary_id',
    'department_id',
    'scheme_id',
    'parent_department_id',
    'invoice_count',
]);

const isNumericInvoiceId = (value) =>
    value != null && String(value).trim() !== '' && /^\d+$/.test(String(value).trim());

const isReimbursementOrSalaryRow = (row) =>
    Boolean(row?.beneficiary_reimbursement_id || row?.salary_id);

const isVendorInvoiceRow = (row) => {
    if (isReimbursementOrSalaryRow(row)) {
        return false;
    }

    return Boolean(
        row?.invoice_unique_id ||
        row?.invoice_beneficiary_name ||
        row?.beneficiary_name ||
        row?.invoice_sanctioned_amount ||
        row?.sanctioned_amount ||
        row?.invoice_total_payable ||
        row?.payment_amount ||
        row?.invoice_id ||
        row?.voucher_order_no
    );
};

/** R = reimbursement, D = invoice, S = salary (salary display parked for now). */
const getPpaRecordType = (row) => {
    const prefix = String(row?.ppa_no || '').trim().charAt(0).toUpperCase();
    if (prefix === 'R' || row?.beneficiary_reimbursement_id) {
        return 'reimbursement';
    }
    if (prefix === 'S' || row?.salary_id) {
        return 'salary';
    }
    return 'invoice';
};

const getVoucherOrderDisplay = (row) => {
    const type = getPpaRecordType(row);
    if (type === 'reimbursement') {
        return row?.beneficiary_reimbursement_id || '-';
    }
    if (type === 'salary') {
        return row?.salary_id || '-';
    }
    return row?.invoice_id || row?.voucher_order_no || '-';
};

const getBeneficiaryDisplay = (row) => {
    const type = getPpaRecordType(row);

    if (type === 'reimbursement') {
        return {
            name: row?.reimbursement_beneficiary_name || '-',
            account: row?.reimbursement_beneficiary_account_no || '-',
            ifsc: row?.reimbursement_beneficiary_ifsc_code || '-',
        };
    }

    if (type === 'salary') {
        return {
            name: '-',
            account: '-',
            ifsc: '-',
        };
    }

    return {
        name: row?.invoice_beneficiary_name || '-',
        account: row?.invoice_beneficiary_account_no || '-',
        ifsc: row?.invoice_beneficiary_ifsc_code || '-',
    };
};

const getVendorInvoiceRecordId = (row) => {
    if (!row) {
        return null;
    }

    const explicitCandidates = [
        row.invoice_unique_id,
        row.sanction_order_invoice_id,
        row.sanctions_order_invoice_id,
        row.sanctions_order_invoices_id,
        row.order_invoice_id,
        row.invoice_db_id,
        row.table_invoice_id,
        row.invoice_table_id,
        row.vendor_invoice_id,
        row.invoice_pk_id,
        row.soi_id,
        row.invoiceId,
    ];

    for (const candidate of explicitCandidates) {
        if (isNumericInvoiceId(candidate)) {
            return candidate;
        }
    }

    if (isNumericInvoiceId(row.invoice_id)) {
        return row.invoice_id;
    }

    for (const [key, value] of Object.entries(row)) {
        if (EXCLUDED_INVOICE_ID_FIELDS.has(key)) {
            continue;
        }

        const normalizedKey = key.toLowerCase();
        const looksLikeInvoiceId =
            normalizedKey.endsWith('_invoice_id') ||
            (normalizedKey.includes('invoice') && normalizedKey.endsWith('id'));

        if (looksLikeInvoiceId && isNumericInvoiceId(value)) {
            return value;
        }
    }

    return null;
};

const ACTION_ICON_GAP = 6;
const ACTION_TWO_ICON_ROW_WIDTH = 62;
const ACTION_COLUMN_WIDTH = '70px';
const DEDUCTION_COLUMN_WIDTH = '170px';

const formatTableAmount = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return AddCommasToAmount(value) || '-';
};

export default function ConsolidateBeneficiaryPpa() {
    const { authData, financialYears } = useSelector((state) => state.authData);
    const expenseDateBasis = useSelector(
        (state) => state.appConfig?.expenseDateBasis || DEFAULT_EXPENSE_DATE_BASIS
    );
    const fyList = financialYears.payload.fy_list;
    const fyIdRef = useRef(localStorage.getItem('globalDateId'));
    const [fyId, setFyId] = useState(fyIdRef.current);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(100);
    const [data, setData] = useState([]);
    const [dataLength, setDataLength] = useState(0);
    const [selectedRowData, setSelectedRowData] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const [invoiceOverlay, setInvoiceOverlay] = useState(true);
    const [invoiceDetails, setInvoiceDetails] = useState(null);
    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const handlePageTrigger = () => {
        fyIdRef.current = localStorage.getItem('globalDateId');
        setFyId(fyIdRef.current);
        setPage(0);
    };

    const [, setAmountFormatTick] = useState(0);

    useEffect(() => {
        const onAmountDivisorChange = () => setAmountFormatTick((tick) => tick + 1);
        window.addEventListener('amount_divisor_changed', onAmountDivisorChange);
        return () => window.removeEventListener('amount_divisor_changed', onAmountDivisorChange);
    }, []);

    const isMulti = authData.user.department_details.is_multi === "0" ? false : true;
    const [filterPayload, setFilterPayload] = useState({
        searchStr: "",
        fromDate: "",
        toDate: "",
        dateColumn: "",
        department_id: "",
        parent_department_id: "",
        parent_department_name: "",
        parent_t2_id: "",
        tier: "",
        consolidate: false,
        ppa_mode: "",
        payment_status: "",
        district_id: "",
        district_name: "",
    })
    const [filterApplied, setFilterApplied] = useState({
        searchStr: "",
        fromDate: "",
        toDate: "",
        dateColumn: "",
        department_id: "",
        parent_department_id: "",
        parent_department_name: "",
        parent_t2_id: "",
        tier: "",
        consolidate: false,
        refresh: false,
        ppa_mode: "",
        payment_status: "",
        district_id: "",
        district_name: "",
    });

    const from_date = filterApplied.fromDate || '';
    const to_date = filterApplied.toDate || '';
    
    const handleInput = async (e) => {
        const {name, value} = e.target;
        setFilterPayload((prev) => ({
            ...prev, [name]: value
        }));
    }

    const handleFilterRemove = async (name) => {
        let newPayload;
        if(name === 'fromDate' || name === 'toDate') {
            newPayload = {
                ...filterApplied,
                fromDate: "",
                toDate: ""
            }
        } else if (name === 'parent_department_id') {
            newPayload = {
                ...filterApplied,
                parent_department_id: "",
                parent_department_name: "",
                parent_t2_id: "",
            };
        } else if (name === 'district_id') {
            newPayload = {
                ...filterApplied,
                district_id: "",
                district_name: "",
            };
        } else {
            newPayload = {
                ...filterApplied,
                [name]: "",
            };
        }
        setFilterPayload(newPayload);
        setFilterApplied(newPayload);
        setPage(0);
    }


    const pageRef = useRef({
        start: 0,
        end: 0,
        currentPage: 1,
    });

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };
    const downloadPPAFun = async (url, ppa_id) => {
        await openPpaDocumentOrToast(ppa_id, url);
    };

    const handleOpenModal = async (rowData) => {
        await fetchPaymentTrail(rowData.ppa_invoice_batch_id, rowData.reimbursement_beneficiary_account_no);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedRowData([]);
    };

    const showInvoiceDetails = async (row) => {
        const invoiceId = getVendorInvoiceRecordId(row);
        if (!invoiceId) {
            toast.error('Invoice id not found for this record', {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }

        try {
            setLoading(true);
            const { data } = await get_invoice_details_by_invoice_id(invoiceId);
            if (data?.status) {
                setInvoiceDetails(data.details);
                setInvoiceOverlay(false);
            } else {
                toast.error(data?.message || 'Unable to fetch invoice details', {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        } catch (error) {
            toast.error(error?.message || 'Unable to fetch invoice details', {
                position: toast.POSITION.TOP_CENTER,
            });
        } finally {
            setLoading(false);
        }
    };

    const bootList = async () => {
        setPage(0);
        setFilterApplied(filterPayload);
        setOpen(false);
    }

    const columns = [
        { Header: '#', accessor: 'sno', style: { width: '70px' } },
        { Header: 'Actions', accessor: 'ppa_url', style: { width: ACTION_COLUMN_WIDTH } },
        { Header: 'PPA Info', accessor: 'ppa_info', style: { width: '200px' } },
        { Header: 'Department (Tier / Code)', accessor: 'department_info', style: { width: '250px' } },
        { Header: <PpaSchemeInfoColumnHeader />, accessor: 'scheme_info', style: { width: '250px' } },
        { Header: 'voucher / order No', accessor: 'invoice_id', style: { width: '200px' } },
        { Header: 'Beneficiary Info', accessor: 'beneficiary_info', style: { width: '220px' } },
        { Header: 'Amount Info', accessor: 'amount_info', style: { width: '200px' } },
        { Header: 'Deduction Details', accessor: 'deduction_info', style: { width: DEDUCTION_COLUMN_WIDTH } },
    ];

    const isWrapColumn = (accessor) => accessor === 'scheme_info' || accessor === 'deduction_info';
    const isActionsColumn = (accessor) => accessor === 'ppa_url';
    const getColumnCellStyle = (column) => ({
        minWidth: column?.style?.width || column.minWidth,
        width: column?.style?.width,
        maxWidth: column?.style?.width,
        whiteSpace: isWrapColumn(column.accessor) ? 'normal' : 'nowrap',
        overflow: isActionsColumn(column.accessor) ? 'visible' : 'hidden',
        textOverflow: isWrapColumn(column.accessor) ? 'clip' : 'ellipsis',
        wordBreak: isWrapColumn(column.accessor) ? 'break-word' : 'normal',
        ...(isActionsColumn(column.accessor) ? { padding: '8px 4px' } : {}),
        ...(column.style || {}),
    });

    const fetchExcelConsolidateRecords = async (department_id, tier) => {
        try {
            setLoading(true);
            const parent_department_id = filterApplied.parent_department_id || '';
            if (!department_id && !parent_department_id) {
                department_id = authData.user.department_id;
            }
            if (!tier) {
                // District-only: do not force JWT tier — all levels in that district
                tier = filterApplied.district_id
                    ? null
                    : authData.user.department_details.department_level;
            } else if (tier === 'all') {
                tier = null;
            }
            const resp = await getExportConsolidateBeneficiaryPPAList({ 
                from_date, 
                to_date, 
                search_str: filterApplied.searchStr, 
                department_id: department_id, 
                parent_department_id,
                tier: tier,
                ppa_mode: filterApplied.ppa_mode,
                payment_status: filterApplied.payment_status,
                fy_id: fyId || localStorage.getItem('globalDateId') || '',
                expense_date_basis: expenseDateBasis,
                district_id: filterApplied.district_id || '',
            });
            await downloadAuthBlobResponse(resp, 'consolidated_beneficiary_ppa.xlsx');
        } catch (error) {
            toast.error(await getBlobErrorMessage(error, 'Failed to export'), {
                position: toast.POSITION.TOP_CENTER,
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchConsolidateRecords = async (limit, offset, department_id, tier) => {
        try {
            setLoading(true);
            const parent_department_id = filterApplied.parent_department_id || '';
            if (!department_id && !parent_department_id) {
                department_id = authData.user.department_id;
            }
            if (!tier) {
                // District-only: do not force JWT tier — all levels in that district
                tier = filterApplied.district_id
                    ? null
                    : authData.user.department_details.department_level;
            } else if (tier === 'all') {
                tier = null;
            }
            const resp = await getConsolidateBeneficiaryPPAList({
                limit, offset, 
                from_date, 
                to_date, 
                search_str: filterApplied.searchStr, 
                department_id, tier,
                parent_department_id,
                ppa_mode: filterApplied.ppa_mode,
                payment_status: filterApplied.payment_status,
                fy_id: fyId || localStorage.getItem('globalDateId') || '',
                expense_date_basis: expenseDateBasis,
                district_id: filterApplied.district_id || '',
            });
            if (resp.data.status) {
                setData(resp.data.result.data);
                setDataLength(Number(resp.data.result.total_count) || 0);
                pageRef.current.currentPage = offset;
                pageRef.current.start = (offset - 1) * limit;
                if(resp.data.result.data.length) {
                    pageRef.current.start += 1;
                }
                pageRef.current.end = (offset * limit) > resp.data.result.total_count ? resp.data.result.total_count : (offset * limit);
            }
        } catch (error) {
            setData([]);
            toast.error(error?.message || 'Failed to fetch consolidate records', {
                position: toast.POSITION.TOP_CENTER,
            });
        } finally {
            setLoading(false)
        }
    }
    
    const fetchPaymentTrail = async (ppa_invoice_batch_id, account_no) => {
        try {
            setLoading(true);
            const response = await getBeneficiaryPPATrail({ ppa_invoice_batch_id, account_no });
            if (response && response.data) {
                setLoading(false);
                setSelectedRowData(response.data.result || []);
            }
        } catch (error) {
            setLoading(false);
            toast.error(error?.message || 'Failed to fetch payment trail', {
                position: toast.POSITION.TOP_CENTER,
            });
        }
    };

    const getPaymentStatus = (payment_mode, flag_success_return) => {
        if (payment_mode === 'online') {
            if (flag_success_return === '1') {
                return { label: 'Success', color: '#d4edda', textColor: '#155724' };
            } else if (flag_success_return === '0') {
                return { label: 'Rejected', color: '#f8d7da', textColor: '#721c24' };
            } else if (flag_success_return === null) {
                return { label: 'Pending', color: '#fff3cd', textColor: '#856404' };
            } else if (flag_success_return === '2') {
                return { label: 'Failed', color: '#f8d7da', textColor: '#721c24' };
            } else if (flag_success_return === '3') {
                return { label: 'On Hold', color: '#f8d7da', textColor: '#856404' };
            } else {
                return { label: 'Sent To Bank', color: '#fff3cd', textColor: '#856404' };
            }
        } else {
            return { label: 'Success', color: '#d4edda', textColor: '#155724' };
        }
    }

    const getPaymentModeChip = (paymentMode) => {
        const mode = String(paymentMode || '').toLowerCase();

        if (mode === 'online') {
            return { label: 'Online', bg: '#e8f0ff', color: '#1f4db8' };
        }

        return { label: 'Offline', bg: '#e6f4ea', color: '#1e7e34' };
    };

    useEffect(() => {
        fetchConsolidateRecords(rowsPerPage, page + 1, filterApplied.department_id, filterApplied.tier);
    }, [
        page,
        rowsPerPage,
        filterApplied.tier,
        filterApplied.department_id,
        filterApplied.parent_department_id,
        filterApplied.fromDate,
        filterApplied.toDate,
        filterApplied.searchStr,
        filterApplied.ppa_mode,
        filterApplied.payment_status,
        filterApplied.district_id,
        fyId,
        expenseDateBasis,
    ])

    
    const displayedRows = data;

    const totalColumnWidth = columns.reduce((total, column) => {
        const columnWidth = parseInt(column?.style?.width, 10) || 0;
        return total + columnWidth;
    }, 0);

    const renderCellValue = (row, column, rowIndex) => {
        if (column.accessor === 'sno') {
            return page * rowsPerPage + rowIndex + 1;
        }

        if (column.accessor === 'ppa_url') {
            return (
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: `${ACTION_ICON_GAP}px`,
                        width: `${ACTION_TWO_ICON_ROW_WIDTH}px`,
                        maxWidth: `${ACTION_TWO_ICON_ROW_WIDTH}px`,
                    }}
                >
                    <Tooltip title="Download PPA" arrow>
                        <IconButton
                            size="small"
                            onClick={() => downloadPPAFun(row.ppa_url, row.id)}
                            sx={{
                                width: 28,
                                height: 28,
                                border: '1px solid #1976d2',
                                color: '#1976d2',
                                borderRadius: '6px',
                            }}
                        >
                            <AiOutlineVerticalAlignBottom size={16} />
                        </IconButton>
                    </Tooltip>
                    {row.payment_mode === 'online' && (
                        <Tooltip title="Payment Transfer Logs" arrow>
                            <IconButton
                                size="small"
                                onClick={() => handleOpenModal(row)}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    border: '1px solid #f59e0b',
                                    color: '#f59e0b',
                                    borderRadius: '6px',
                                }}
                            >
                                <HistoryOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {isVendorInvoiceRow(row) ? (
                        <Tooltip title="View Invoice Details" arrow>
                            <IconButton
                                size="small"
                                onClick={() => showInvoiceDetails(row)}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    border: '1px solid #17a2b8',
                                    color: '#17a2b8',
                                    borderRadius: '6px',
                                }}
                            >
                                <RemoveRedEyeOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    ) : null}
                </div>
            );
        }

        if (column.accessor === 'department_info') {
            return <DepartmentIdentityCell row={row} />;
        }

        if (column.accessor === 'scheme_info') {
            return <PpaSchemeInfoCell row={row} />;
        }

        if (column.accessor === 'invoice_id') {
            return getVoucherOrderDisplay(row);
        }

        if (column.accessor === 'beneficiary_info') {
            const { name, account, ifsc } = getBeneficiaryDisplay(row);
            return (
                <div style={{ lineHeight: 1.25 }}>
                    <div style={{ fontWeight: 600 }}>{name}</div>
                    <div style={{ color: '#6b7280', marginTop: '4px' }}>A/C: {account}</div>
                    <div style={{ color: '#6b7280', marginTop: '2px' }}>IFSC: {ifsc}</div>
                </div>
            );
        }

        if (column.accessor === 'ppa_info') {
            const modeChip = getPaymentModeChip(row?.payment_mode);
            const statusMeta = getPaymentStatus(row.payment_mode, row.flag_success_return);
            const approvalDate = row?.l3_approval_datetime
                ? moment(row.l3_approval_datetime, [
                    'YYYY-MM-DD HH:mm:ss',
                    'YYYY-MM-DDTHH:mm:ssZ',
                    'YYYY-MM-DDTHH:mm:ss.SSSZ',
                    moment.ISO_8601,
                ]).format('DD MMM YYYY, hh:mm A')
                : '-';

            return (
                <div style={{ lineHeight: 1.25 }}>
                    <div style={{ fontWeight: 600 }}>PPA: {row?.ppa_no || '-'}</div>
                    <div style={{ marginTop: '4px' }}>
                        <span
                            style={{
                                padding: '3px 8px',
                                borderRadius: '999px',
                                backgroundColor: modeChip.bg,
                                color: modeChip.color,
                                fontSize: '12px',
                                fontWeight: 600,
                                display: 'inline-block',
                            }}
                        >
                            {modeChip.label}
                        </span>
                    </div>
                    <div style={{ color: '#6b7280', marginTop: '4px', fontSize: '12px' }}>
                        {approvalDate}
                    </div>
                    <div style={{ marginTop: '6px' }}>
                        {statusMeta ? (
                            <span
                                style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    backgroundColor: statusMeta.color,
                                    color: statusMeta.textColor,
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    display: 'inline-block',
                                }}
                            >
                                {statusMeta.label}
                            </span>
                        ) : (
                            <span style={{ color: '#9ca3af', fontSize: '12px' }}>N/A</span>
                        )}
                    </div>
                </div>
            );
        }

        if (column.accessor === 'amount_info') {
            const type = getPpaRecordType(row);
            const sanctioned = type === 'reimbursement'
                ? (row?.reimbursement_gross_amount ?? row?.invoice_sanctioned_amount ?? row?.sanctioned_amount)
                : (row?.invoice_sanctioned_amount ?? row?.sanctioned_amount ?? row?.reimbursement_gross_amount);
            const deduction = type === 'reimbursement'
                ? (row?.reimbursement_deduction_amount ?? row?.invoice_total_deduction ?? row?.deduction_amount)
                : (row?.invoice_total_deduction ?? row?.deduction_amount ?? row?.reimbursement_deduction_amount);
            const payment = type === 'reimbursement'
                ? (row?.reimbursement_net_amount ?? row?.invoice_total_payable ?? row?.payment_amount)
                : (row?.invoice_total_payable ?? row?.payment_amount ?? row?.reimbursement_net_amount);
            return (
                <div style={{ lineHeight: 1.25 }}>
                    <div style={{ fontWeight: 600 }}>Sanctioned: {formatTableAmount(sanctioned)}</div>
                    <div style={{ color: '#6b7280', marginTop: '4px' }}>Deduction: {formatTableAmount(deduction)}</div>
                    <div style={{ color: '#6b7280', marginTop: '2px' }}>Payment: {formatTableAmount(payment)}</div>
                </div>
            );
        }

        if (column.accessor === 'deduction_info') {
            return <PpaDeductionDetailsCell row={row} />;
        }

        if (column.accessor === 'l3_approval_datetime') {
            return row?.l3_approval_datetime
                ? moment(row.l3_approval_datetime).format('DD-MM-YYYY hh:mm A')
                : '-';
        }

        return row?.[column.accessor] ?? '-';
    };


    return (
        <>
            {loading && <Spinner />}
            <SubHeader fy_list={fyList} handlePageTrigger={handlePageTrigger} />
            <div className="p-3">
                <div className="row" style={{ padding: "10px 16px" }}>
                    <div className="col-md-6">
                        <h4 style={{ color: "#28326f", margin:0}}>Consolidate PPA Report</h4>
                        <CustomBreadcrumbs linksArray={[]} title="Consolidate PPA Report"/>
                    </div>
                    <div className="col-md-6">
                        <div className="d-flex gap-2" style={{ width: "auto", float: "right" }}>
                            <Button variant="contained" className="navigation_button" onClick={() => fetchExcelConsolidateRecords(filterApplied.department_id, filterApplied.tier)}> Export Excel</Button>
                        </div>
                    </div>
                </div>
                <DashboardFilter 
                    openFilterModal={setOpen} 
                    filterPayload={filterApplied} 
                    handleFilterRemove={handleFilterRemove}
                    isTierRemovalRequired={true}
                />
                <div className="row">
                    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                        <PpaSchemeInfoExpandProvider>
                        <TableContainer sx={{ maxHeight: '72vh', overflowX: 'auto', overflowY: 'auto' }}>
                            <Table
                                stickyHeader
                                aria-label="sticky table"
                                sx={{
                                    tableLayout: 'fixed',
                                    width: `${totalColumnWidth}px`,
                                    minWidth: `${totalColumnWidth}px`,
                                }}
                            >
                            <colgroup>
                                {columns.map((column) => (
                                    <col key={`col_${column.accessor}_${column?.style?.width || 'auto'}`} style={{ width: column?.style?.width }} />
                                ))}
                            </colgroup>
                            <TableHead>
                                <TableRow>
                                {columns.map((column) => (
                                    <TableCell
                                    key={`head_${column.id || column.accessor}_${column?.style?.width || 'auto'}`}
                                    align={column.align}
                                    style={getColumnCellStyle(column)}
                                    >
                                    {column.label || column.Header}
                                    </TableCell>
                                ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {displayedRows
                                .map((row, rowIndex) => {
                                    return (
                                    <TableRow hover role="checkbox" tabIndex={-1} key={`row_${row.id || 'noid'}_${row.ppa_no || 'noppa'}_${row.invoice_id || 'noinvoice'}_${rowIndex}`}>
                                        {columns.map((column) => (
                                            <TableCell
                                                key={`cell_${column.accessor}_${row.id || 'noid'}_${row.ppa_no || 'noppa'}_${row.invoice_id || 'noinvoice'}_${rowIndex}`}
                                                align={column.align}
                                                style={getColumnCellStyle(column)}
                                            >
                                                {renderCellValue(row, column, rowIndex)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                    );
                                })}
                            </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[100, 250, 500]}
                            component="div"
                            count={dataLength || data.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                        </PpaSchemeInfoExpandProvider>
                    </Paper>
                </div>
            </div>
            <PaymentTrailModal
                open={openModal}
                onClose={handleCloseModal}
                trails={selectedRowData}
            />

            <InvoiceOverlay
                overlay={invoiceOverlay}
                setOverlay={setInvoiceOverlay}
                invoiceDetails={invoiceDetails}
            />

            <MainFilter 
                open={open} 
                handleClose={handleClose} 
                handleInput={handleInput} 
                handleOpen={handleOpen} 
                handleFilterClick={bootList} 
                filterPayload={filterPayload}
                setConsolidate={setFilterPayload}
                type="consolidatePPA"/>
        </>
    )

}